import { useCallback, useEffect, useState } from "react";
import { api } from "../utils/axios-instance";
import { useDepartment } from "../stores/departmentStore";

// Departments are no longer fetched here — they previously came from a
// second, Django-ORM-backed /departments/ endpoint that could drift from the
// Firestore-backed list everywhere else in the app. This hook now reads from
// the same useDepartment store as the rest of TwoKey.
//
// NOTE on `department`: this hook intentionally does NOT take a department
// filter param. The tickets board UI supports selecting *multiple*
// departments at once, and needs each department chip to keep showing its
// own true ticket count even while other departments are selected/excluded
// — so department filtering happens client-side in TicketsBoard against the
// full fetched board, not as a server-side query param here.
export function useTickets({ visibility, search }) {
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);

  const departments = useDepartment((s) => s.departments);
  const departmentsLoading = useDepartment((s) => s.loading);
  const listDepartments = useDepartment((s) => s.listDepartments);

  useEffect(() => {
    if (!departments.length && !departmentsLoading) {
      listDepartments();
    }
  }, [departments.length, departmentsLoading, listDepartments]);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { visibility };
      if (search) params.search = search;

      const res = await api.get("/tickets/board/", { params });
      setBoard(res.data);
    } catch (err) {
      console.error("Failed to load ticket board", err);
    } finally {
      setLoading(false);
    }
  }, [visibility, search]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  /** Replaces the given ticket everywhere it currently sits in `board`
   * (there's only ever one status bucket it can be in, but we don't know
   * which without scanning) with `patch` merged over its previous data. */
  const patchTicketInBoard = useCallback((ticketId, patch) => {
    setBoard((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((statusKey) => {
        next[statusKey] = next[statusKey].map((t) =>
          t.id === ticketId ? { ...t, ...patch } : t
        );
      });
      return next;
    });
  }, []);

  const createTicket = useCallback(async (payload) => {
    // payload.department_id should be the Firestore department id (dept.id
    // from useDepartment), matching TicketCreateSerializer in the backend.
    const res = await api.post("/tickets/", payload);
    return res.data;
  }, []);

  const moveTicket = useCallback(
    async (ticketId, newStatus) => {
      // Optimistic UI update
      setBoard((prev) => {
        const next = { ...prev };
        let movedTicket = null;

        Object.keys(next).forEach((statusKey) => {
          const idx = next[statusKey].findIndex((t) => t.id === ticketId);
          if (idx !== -1) {
            [movedTicket] = next[statusKey].splice(idx, 1);
            next[statusKey] = [...next[statusKey]];
          }
        });

        if (movedTicket) {
          movedTicket = { ...movedTicket, status: newStatus };
          next[newStatus] = [movedTicket, ...(next[newStatus] || [])];
        }

        return next;
      });

      try {
        await api.patch(`/tickets/${ticketId}/move/`, {
          status: newStatus,
          order: 0,
        });
      } catch (err) {
        console.error("Move failed, reverting", err);
        fetchBoard(); // rollback by refetching source of truth
      }
    },
    [fetchBoard]
  );

  /** PATCH /tickets/{id}/ — title/description/department/priority/due_date.
   * Used by the edit dialog's "Save changes". */
  const updateTicket = useCallback(
    async (ticketId, changes) => {
      try {
        const res = await api.patch(`/tickets/${ticketId}/`, changes);
        patchTicketInBoard(ticketId, res.data);
        return res.data;
      } catch (err) {
        console.error("Update failed, reverting", err);
        fetchBoard();
        throw err;
      }
    },
    [patchTicketInBoard, fetchBoard]
  );

  /** PATCH /tickets/{id}/assign/ */
  const assignTicket = useCallback(
    async (ticketId, assigneeUid) => {
      try {
        const res = await api.patch(`/tickets/${ticketId}/assign/`, {
          assignee_uid: assigneeUid,
        });
        patchTicketInBoard(ticketId, res.data);
        return res.data;
      } catch (err) {
        console.error("Assign failed, reverting", err);
        fetchBoard();
        throw err;
      }
    },
    [patchTicketInBoard, fetchBoard]
  );

  /** DELETE /tickets/{id}/ */
  const deleteTicket = useCallback(
    async (ticketId) => {
      const previousBoard = board;
      setBoard((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((statusKey) => {
          next[statusKey] = next[statusKey].filter((t) => t.id !== ticketId);
        });
        return next;
      });

      try {
        await api.delete(`/tickets/${ticketId}/`);
      } catch (err) {
        console.error("Delete failed, reverting", err);
        setBoard(previousBoard);
        throw err;
      }
    },
    [board]
  );

  return {
    board,
    departments,
    departmentsLoading,
    loading,
    createTicket,
    updateTicket,
    deleteTicket,
    moveTicket,
    assignTicket,
    refetch: fetchBoard,
  };
}
